import type { TypedDocumentNode } from '@apollo/client';
import { gql, useFragment } from '@apollo/client';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import { catchAll } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_MY_CARDS_CARDS_TYPE,
  ANY_SPORT_MY_CARDS_COLLECTIONS,
  ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import type { CardOrCollectionRaritySwitcher_currentUser } from './__generated__/index.graphql';
import { MyCardsRaritySwitcherBox } from './ui';

type Props = {
  type: Nullable<Rarity>;
};

export const CardOrCollectionRaritySwitcher = ({ type }: Props) => {
  const { currentUser: contextCurrentUser } = useCurrentUserContext();
  const { data: currentUser } = useFragment({
    // eslint-disable-next-line sorare/no-detached-fragments
    fragment: CardOrCollectionRaritySwitcher.fragments.currentUser,
    from: contextCurrentUser || null,
  });
  const { cardCounts } = contextCurrentUser || {};
  const { cardCollections } = currentUser || {};
  const { raritiesCount } = cardCollections || {};

  const cardsCountByRarity = {
    [Rarity.common]: cardCounts?.common || 0,
    [Rarity.limited]: cardCounts?.limited || 0,
    [Rarity.rare]: cardCounts?.rare || 0,
    [Rarity.super_rare]: cardCounts?.superRare || 0,
    [Rarity.unique]: cardCounts?.unique || 0,
  };

  const collectionsCountByRarity = {
    [Rarity.common]:
      raritiesCount?.find(c => c?.rarity === Rarity.common)?.count || 0,
    [Rarity.limited]:
      raritiesCount?.find(c => c?.rarity === Rarity.limited)?.count || 0,
    [Rarity.rare]:
      raritiesCount?.find(c => c?.rarity === Rarity.rare)?.count || 0,
    [Rarity.super_rare]:
      raritiesCount?.find(c => c?.rarity === Rarity.super_rare)?.count || 0,
    [Rarity.unique]:
      raritiesCount?.find(c => c?.rarity === Rarity.unique)?.count || 0,
  };

  const { generateSportContextPath, matchAnySportPath } = useSportContext();
  const location = useLocation();
  const matchCollectionsPage = matchAnySportPath(
    catchAll(ANY_SPORT_MY_CARDS_COLLECTIONS),
    location.pathname
  );

  return (
    <Horizontal>
      {Object.entries(
        matchCollectionsPage ? collectionsCountByRarity : cardsCountByRarity
      )
        .filter(([, value]) => !!value)
        .map(([key, value]) => (
          <MyCardsRaritySwitcherBox
            key={key}
            to={generateSportContextPath(
              matchCollectionsPage
                ? ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE
                : ANY_SPORT_MY_CARDS_CARDS_TYPE,
              {
                params: {
                  type: key,
                },
              }
            )}
            className={classNames({
              active: type === key,
            })}
          >
            <NewScarcityIcon
              rarity={key as keyof typeof cardsCountByRarity}
              size={5}
            />
            <LabelM bold>{value}</LabelM>
          </MyCardsRaritySwitcherBox>
        ))}
    </Horizontal>
  );
};

CardOrCollectionRaritySwitcher.fragments = {
  currentUser: gql`
    fragment CardOrCollectionRaritySwitcher_currentUser on CurrentUser {
      slug
      cardCollections(startedOnly: true) {
        raritiesCount {
          rarity
          count
        }
      }
    }
  ` as TypedDocumentNode<CardOrCollectionRaritySwitcher_currentUser>,
};
